<div class="vocabulary-editor">
  <header class="editor-header">
    <nav class="editor-navigation" aria-label="Terminology editor views">
      <button
        type="button"
        [class.editor-navigation__active]="workspaceView === 'edit'"
        [attr.aria-current]="workspaceView === 'edit' ? 'page' : null"
        (click)="selectWorkspace('edit')"
        [disabled]="comparingCurrent()"
      >
        Edit wording
      </button>
      <button
        type="button"
        [class.editor-navigation__active]="workspaceView === 'impact'"
        [attr.aria-current]="workspaceView === 'impact' ? 'page' : null"
        (click)="selectWorkspace('impact')"
        [disabled]="comparingCurrent()"
      >
        Where used <span>{{ usageCount }}</span>
      </button>
      <details
        *ngIf="!comparingCurrent()"
        #moreMenu
        class="editor-more"
        (keydown.escape)="closeMore($event)"
      >
        <summary>More <span aria-hidden="true">▾</span></summary>
        <div class="editor-more__menu">
          <button type="button" (click)="selectWorkspace('history')">
            Activity &amp; health
          </button>
          <button type="button" (click)="selectWorkspace('technical')">
            Technical details
          </button>
          <button
            type="button"
            data-testid="terminology-reset"
            (click)="resetChanges(); closeMore()"
            [disabled]="
              !canEdit || busy || comparingCurrent() || !hasUnsavedChanges
            "
          >
            Reset changes
          </button>
        </div>
      </details>
    </nav>
    <div class="editor-save-actions">
      <span data-testid="terminology-editor-status" role="status">{{
        busy
          ? "Working…"
          : hasUnsavedChanges
            ? "Unsaved changes"
            : isInitialWording
              ? "Product default"
              : "Current"
      }}</span>
      <button
        type="button"
        class="editor-button editor-button--primary"
        data-testid="terminology-save-wording"
        (click)="saveWording()"
        aria-keyshortcuts="Control+S Meta+S"
        title="Save wording (Ctrl/Cmd+S)"
        [disabled]="!canSaveWording"
      >
        {{ isSaving() ? "Saving…" : "Save wording" }}
      </button>
      <span
        class="editor-edit-actions"
        role="group"
        aria-label="Undo and redo unsaved edits"
      >
        <button
          type="button"
          class="editor-button editor-button--secondary editor-button--icon"
          data-testid="terminology-undo"
          aria-label="Undo last edit"
          aria-keyshortcuts="Control+Z Meta+Z"
          title="Undo (Ctrl/Cmd+Z)"
          [disabled]="!canUndo"
          (click)="undoEdit()"
        >
          <span aria-hidden="true">↶</span>
        </button>
        <button
          type="button"
          class="editor-button editor-button--secondary editor-button--icon"
          data-testid="terminology-redo"
          aria-label="Redo last edit"
          aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y"
          title="Redo (Ctrl/Cmd+Shift+Z or Ctrl+Y)"
          [disabled]="!canRedo"
          (click)="redoEdit()"
        >
          <span aria-hidden="true">↷</span>
        </button>
      </span>
      <button
        type="button"
        class="editor-button editor-button--secondary"
        data-testid="terminology-editor-help"
        aria-haspopup="dialog"
        [attr.aria-expanded]="helpOpen"
        (click)="openHelp($event)"
      >
        Help
      </button>
    </div>
  </header>
  <span
    class="visually-hidden"
    role="status"
    aria-live="polite"
    data-testid="terminology-edit-history-status"
    >{{ historyMessage() }}</span
  >
  <section
    *ngIf="helpOpen"
    #helpPanel
    class="editing-help"
    role="dialog"
    aria-labelledby="terminology-help-heading"
    data-testid="terminology-editing-help"
    tabindex="-1"
    (keydown.escape)="closeHelp()"
  >
    <div>
      <strong id="terminology-help-heading">Editing wording</strong
      ><button type="button" (click)="closeHelp()">Close help</button>
    </div>
    <p>
      Edit one selected form at a time. Each form is authored independently;
      changing Name does not generate other forms.
    </p>
    <p>
      Save wording replaces active wording in this program pack immediately.
      Unsaved edits stay in this editor. Any compatibility drafts for other
      forms remain separate.
    </p>
    <p>
      Undo and Redo restore unsaved wording, notes and linked behavior in this
      form. Save, choosing current wording or changing editing context starts a
      new history. Ctrl/Cmd+S saves; Ctrl/Cmd+Z undoes and Ctrl/Cmd+Shift+Z
      redoes. Reset changes is in More.
    </p>
    <p>
      Use Where used to inspect known consumers. A change note is optional.
      References are available only where registered constraints allow them.
    </p>
  </section>

  <div
    class="editor-message editor-message--error"
    *ngIf="editorError() as error"
    role="alert"
    aria-live="assertive"
  >
    <div>
      <strong>{{ error.title }}</strong>
      <span *ngFor="let message of error.messages">{{ message }}</span>
    </div>
    <button
      type="button"
      data-testid="terminology-compare-current"
      *ngIf="
        error.statusCode === 409 ||
        error.statusCode === 412 ||
        comparisonLoadFailed()
      "
      (click)="reloadCurrentWording()"
      [disabled]="busy"
    >
      Compare current wording
    </button>
  </div>
  <div
    class="editor-message editor-message--success"
    data-testid="terminology-save-success"
    *ngIf="saveMessage() as message"
    role="status"
    aria-live="polite"
  >
    {{ message }}
  </div>

  <div
    *ngIf="editRestriction as restriction"
    class="editing-restriction"
    role="status"
  >
    {{ restriction }}
  </div>
  <div *ngIf="isReloading()" class="editor-loading" role="status">
    Loading current wording; your proposed text is preserved…
  </div>
  <section
    *ngIf="comparingCurrent()"
    class="stale-comparison"
    data-testid="terminology-stale-comparison"
    aria-labelledby="terminology-comparison-heading"
  >
    <header class="comparison-heading">
      <h2 #comparisonHeading tabindex="-1" id="terminology-comparison-heading">
        Review the latest wording
      </h2>
      <button
        type="button"
        class="editor-button editor-button--secondary"
        data-testid="terminology-refresh-comparison"
        [disabled]="busy"
        (click)="reloadCurrentWording()"
      >
        Refresh latest
      </button>
    </header>
    <div
      class="comparison-body"
      tabindex="0"
      role="region"
      aria-label="Wording comparison details"
    >
      <p
        class="comparison-summary"
        *ngIf="comparisonLoadFailed()"
        role="status"
      >
        Latest wording could not be refreshed. Retry before choosing a version.
      </p>
      <details class="comparison-original">
        <summary>
          Wording you started from · {{ comparisonOriginalSource }}
        </summary>
        <span class="comparison-behavior" *ngIf="isPhrase">{{
          comparisonOriginalBehavior
        }}</span>
        <p
          data-testid="terminology-comparison-original"
          [textContent]="comparisonOriginalText"
        ></p>
      </details>
      <p class="comparison-summary">
        {{
          comparisonWordingDiff.hasChanges
            ? "Highlighted text differs between the latest wording and your proposal."
            : "Your text matches the latest wording. Review the link behavior and note below."
        }}
      </p>
      <p class="comparison-summary" *ngIf="comparisonWordingDiff.isCoarse">
        Large changes are highlighted as whole sections. Complete wording is
        shown.
      </p>
      <div class="comparison-values">
        <section aria-labelledby="terminology-comparison-current-heading">
          <h3 id="terminology-comparison-current-heading">
            {{
              comparisonLoadFailed() || isReloading()
                ? "Last loaded current wording"
                : "Latest current wording"
            }}
          </h3>
          <span class="comparison-behavior"
            >{{ comparisonCurrentSource
            }}<ng-container *ngIf="isPhrase">
              · {{ comparisonCurrentBehavior }}</ng-container
            ></span
          >
          <p data-testid="terminology-comparison-current">
            <span
              *ngFor="let segment of comparisonWordingDiff.currentSegments"
              [class.comparison-change]="segment.changed"
              >{{ segment.text }}</span
            >
          </p>
        </section>
        <section aria-labelledby="terminology-comparison-proposal-heading">
          <h3 id="terminology-comparison-proposal-heading">Your proposal</h3>
          <span class="comparison-behavior" *ngIf="isPhrase">{{
            comparisonProposalBehavior
          }}</span>
          <p data-testid="terminology-comparison-proposal">
            <span
              *ngFor="let segment of comparisonWordingDiff.proposalSegments"
              [class.comparison-change]="segment.changed"
              >{{ segment.text }}</span
            >
          </p>
          <div class="comparison-note">
            <strong>Your unsaved change note</strong>
            <p
              data-testid="terminology-comparison-note"
              [textContent]="
                changeReason.trim() ? changeReason : 'No change note added.'
              "
            ></p>
          </div>
        </section>
      </div>
    </div>
    <div
      class="comparison-actions"
      *ngIf="!confirmingUseCurrent(); else discardReview"
    >
      <button
        #useCurrentButton
        type="button"
        class="editor-button editor-button--secondary"
        data-testid="terminology-use-current"
        (click)="requestUseCurrentWording()"
        [disabled]="!canResolveComparison"
      >
        Use current wording
      </button>
      <button
        type="button"
        class="editor-button editor-button--primary"
        data-testid="terminology-keep-proposal"
        (click)="keepMyWording()"
        [disabled]="!canEdit || !canResolveComparison"
      >
        Keep my wording
      </button>
      <span
        >Keep preserves your wording and note for editing. Save is a separate
        step.</span
      >
    </div>
    <ng-template #discardReview>
      <section
        #discardConfirmation
        class="comparison-discard"
        tabindex="-1"
        role="group"
        aria-labelledby="terminology-discard-heading"
        (keydown.escape)="cancelUseCurrentWording($event)"
      >
        <strong id="terminology-discard-heading"
          >Discard your proposed changes and note?</strong
        >
        <p>
          Use the latest loaded wording. Your proposal and note will be removed.
        </p>
        <div class="comparison-actions">
          <button
            type="button"
            class="editor-button editor-button--secondary"
            data-testid="terminology-cancel-use-current"
            (click)="cancelUseCurrentWording()"
          >
            Cancel
          </button>
          <button
            type="button"
            class="editor-button editor-button--secondary"
            data-testid="terminology-confirm-use-current"
            [disabled]="!canResolveComparison"
            (click)="useCurrentWording()"
          >
            Discard my changes
          </button>
        </div>
      </section>
    </ng-template>
  </section>
  <ng-template #wordingErrors>
    <div
      *ngIf="fieldErrors().length"
      id="terminology-wording-errors"
      class="field-errors"
      role="alert"
    >
      <span *ngFor="let message of fieldErrors()">{{ message }}</span>
      <div
        class="reference-repair-actions"
        *ngIf="
          missingRequiredReferenceOptions.length ||
          invalidReferenceOccurrences.length
        "
      >
        <button
          type="button"
          *ngFor="let option of missingRequiredReferenceOptions"
          data-testid="terminology-add-required-reference"
          [disabled]="!canChangeReferences || referenceAppendBlockedReason"
          (click)="addRequiredReference(option)"
          [title]="referenceAppendBlockedReason || option.tokenCode"
        >
          Add required link: {{ option.currentText }}
        </button>
        <span
          *ngIf="
            missingRequiredReferenceOptions.length &&
            referenceAppendBlockedReason
          "
          >{{ referenceAppendBlockedReason }}</span
        >
        <button
          type="button"
          *ngFor="
            let reference of invalidReferenceOccurrences;
            trackBy: trackReferenceOccurrence
          "
          data-testid="terminology-find-invalid-reference"
          [disabled]="!canChangeReferences"
          (click)="findReference(reference)"
        >
          Find {{ reference.tokenCode }} ({{ reference.ordinal }})
        </button>
      </div>
    </div>
  </ng-template>
  <ng-container *ngIf="!comparingCurrent()" [ngSwitch]="workspaceView">
    <div
      *ngSwitchCase="'edit'"
      class="editor-frame"
      [class.editor-frame--family]="!isPhrase"
    >
      <ng-template #referenceRail>
        <aside
          class="editor-rail"
          aria-label="Available terminology references"
        >
          <div class="region-heading">
            <div>
              <h2>Linked wording</h2>
              <span>Choose an authorized concept form</span>
            </div>
          </div>
          <label class="reference-search">
            <span class="visually-hidden">Search available concept forms</span>
            <input
              type="search"
              [(ngModel)]="referenceSearch"
              placeholder="Search concept forms"
              autocomplete="off"
            />
          </label>
          <div
            class="reference-target"
            data-testid="terminology-reference-target"
            role="status"
          >
            <strong>{{
              referenceEditPlan.selectedText
                ? referenceEditPlan.isReplacement
                  ? "Replace linked wording"
                  : "Replace selected text"
                : referenceEditPlan.start === draftContent.length
                  ? "Insert at end"
                  : "Insert at cursor"
            }}</strong>
            <code *ngIf="referenceEditPlan.selectedText">{{
              referenceEditPlan.selectedText
            }}</code>
            <span *ngIf="referenceEditPlan.blockedReason">{{
              referenceEditPlan.blockedReason
            }}</span>
            <button
              type="button"
              *ngIf="
                referenceEditPlan.selectedText &&
                !referenceEditPlan.blockedReason
              "
              [disabled]="!canChangeReferences"
              (click)="chooseInsertionAtEnd()"
            >
              Insert at end instead
            </button>
          </div>
          <div class="rail-list rail-list--references">
            <div
              class="reference-row"
              *ngFor="let option of filteredReferenceOptions"
            >
              <span class="family-row__identity">
                <strong>{{ option.currentText }}</strong>
                <span>{{
                  toDisplayLabel(option.grammaticalFormCode || option.kindCode)
                }}</span>
              </span>
              <button
                type="button"
                (click)="insertReference(option)"
                [disabled]="
                  !canChangeReferences ||
                  referenceEditPlan.blockedReason ||
                  !detail.constraints.allowReferences
                "
              >
                {{ referenceEditPlan.selectedText ? "Replace" : "Insert" }}
              </button>
            </div>
            <div
              class="rail-empty"
              *ngIf="filteredReferenceOptions.length === 0"
            >
              No available references match.
              <button
                type="button"
                *ngIf="referenceSearch.trim()"
                (click)="referenceSearch = ''"
              >
                Clear search
              </button>
            </div>
          </div>
        </aside>
      </ng-template>

      <section class="editor-compose" aria-labelledby="edit-wording-heading">
        <div class="region-heading">
          <div>
            <h2 id="edit-wording-heading">
              {{ isPhrase ? "Edit wording" : "Independent forms" }}
            </h2>
            <span *ngIf="!isPhrase">Each form is saved independently</span>
          </div>
          <span *ngIf="definition.currentEffectiveStartUtc"
            >Active since
            {{ definition.currentEffectiveStartUtc | date: "mediumDate" }}</span
          >
        </div>

        <p
          *ngIf="isInitialWording"
          class="initial-wording-notice"
          data-testid="terminology-initial-wording"
        >
          Using the product default. Save wording creates this pack’s first
          override.
        </p>
        <details
          *ngIf="isPhrase && detail.constraints.allowReferences"
          #referenceInsertion
          class="reference-insertion"
          (toggle)="captureReferenceSelection()"
        >
          <summary>Insert linked wording</summary>
          <ng-container [ngTemplateOutlet]="referenceRail"></ng-container>
        </details>
        <div class="editor-fields">
          <div
            *ngIf="isPhrase"
            class="current-wording"
            data-testid="terminology-current-wording"
          >
            <span>Current wording</span>
            <strong>{{ definition.currentText }}</strong>
            <span
              class="resolved-current"
              *ngIf="
                isPhrase && definition.currentReferenceTokenCodes.length > 0
              "
            >
              Illustrative expansion: “{{ resolvedPublished.text }}”
            </span>
          </div>

          <table
            *ngIf="!isPhrase"
            class="family-grid"
            data-testid="terminology-family-grid"
          >
            <caption class="visually-hidden">
              Independent forms: current and proposed wording
            </caption>
            <thead>
              <tr>
                <th scope="col">Form</th>
                <th scope="col">Current</th>
                <th scope="col">New</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let member of formMembers"
                [class.selected]="
                  member.definitionId === definition.definitionId
                "
              >
                <th scope="row">
                  <button
                    type="button"
                    (click)="selectDefinition(member.definitionId)"
                    [disabled]="busy"
                    [attr.aria-current]="
                      member.definitionId === definition.definitionId
                        ? 'true'
                        : null
                    "
                    [attr.aria-label]="'Edit ' + formLabel(member)"
                  >
                    {{ formLabel(member) }}
                  </button>
                </th>
                <td>
                  {{
                    member.definitionId === definition.definitionId
                      ? definition.currentText
                      : member.currentText
                  }}
                </td>
                <td>
                  <ng-container
                    *ngIf="
                      member.definitionId === definition.definitionId;
                      else unchangedForm
                    "
                  >
                    <input
                      #wordingInput
                      type="text"
                      data-testid="terminology-wording-input"
                      [attr.aria-label]="'New wording for ' + formLabel(member)"
                      [(ngModel)]="draftContent"
                      [disabled]="!canEdit || busy || comparingCurrent()"
                      [attr.aria-invalid]="fieldErrors().length ? 'true' : null"
                      [attr.aria-describedby]="
                        fieldErrors().length
                          ? 'terminology-wording-errors'
                          : null
                      "
                      (focus)="focusEditField('wording')"
                      (select)="captureReferenceSelection()"
                      (click)="captureReferenceSelection()"
                      (keyup)="captureReferenceSelection()"
                      (compositionstart)="startComposition('wording')"
                      (compositionend)="finishComposition('wording')"
                      (beforeinput)="beginTextEdit('wording', $event)"
                      (blur)="endTypingGroup()"
                      (ngModelChange)="onTextChanged('wording')"
                      autocomplete="off"
                    />
                    <ng-container
                      [ngTemplateOutlet]="wordingErrors"
                    ></ng-container>
                  </ng-container>
                  <ng-template #unchangedForm
                    ><span class="unchanged-form">Unchanged</span></ng-template
                  >
                </td>
              </tr>
            </tbody>
          </table>
          <label *ngIf="isPhrase" class="editor-field editor-field--wording">
            <span class="field-label">
              <span>New wording</span>
              <span *ngIf="shouldShowCharacterCount"
                >{{ characterCount }} / {{ maximumLength }}</span
              >
            </span>
            <textarea
              *ngIf="isPhrase"
              #wordingInput
              data-testid="terminology-wording-input"
              [(ngModel)]="draftContent"
              rows="4"
              [disabled]="!canEdit || busy || comparingCurrent()"
              [attr.aria-invalid]="fieldErrors().length ? 'true' : null"
              [attr.aria-describedby]="
                fieldErrors().length ? 'terminology-wording-errors' : null
              "
              (focus)="focusEditField('wording')"
              (select)="captureReferenceSelection()"
              (click)="captureReferenceSelection()"
              (keyup)="captureReferenceSelection()"
              (compositionstart)="startComposition('wording')"
              (compositionend)="finishComposition('wording')"
              (beforeinput)="beginTextEdit('wording', $event)"
              (blur)="endTypingGroup()"
              (ngModelChange)="onTextChanged('wording')"
            ></textarea>
            <small
              class="field-help"
              *ngIf="minimumLength !== null && minimumLength > 1"
            >
              At least {{ minimumLength }} characters required.
            </small>
          </label>
          <ng-container
            *ngIf="isPhrase"
            [ngTemplateOutlet]="wordingErrors"
          ></ng-container>

          <div class="resolved-line" *ngIf="isPhrase">
            <strong>Illustrative expansion</strong
            ><span>{{ resolvedDraft.text }}</span>
            <small
              >Uses the loaded reference values. Save validates the full
              dependency graph.</small
            >
          </div>

          <div
            class="draft-reference-check"
            *ngIf="isPhrase && wordingReferences.length > 0"
            aria-label="Linked wording check"
          >
            <strong class="draft-reference-check__heading"
              >Linked wording check</strong
            >
            <span
              class="draft-reference-check__item"
              *ngFor="
                let reference of wordingReferences;
                trackBy: trackReferenceOccurrence
              "
              [class.draft-reference-check__item--invalid]="!reference.isKnown"
            >
              <code>{{ reference.tokenCode }}</code>
              <span>{{
                reference.resolvedText || "Not available in the active scope"
              }}</span>
              <strong>{{
                reference.isKnown ? "Loaded" : "Unavailable"
              }}</strong>
              <button
                type="button"
                data-testid="terminology-find-reference"
                [attr.aria-label]="
                  'Find reference ' +
                  reference.ordinal +
                  ': ' +
                  reference.tokenCode
                "
                [disabled]="!canChangeReferences"
                (click)="findReference(reference)"
              >
                Find {{ reference.ordinal }}
              </button>
            </span>
          </div>

          <fieldset
            class="inheritance-options"
            *ngIf="supportsInheritanceChoice"
          >
            <legend>Linked wording behavior</legend>
            <label
              [class.inheritance-option--selected]="
                inheritanceMode === 'composed'
              "
            >
              <input
                type="radio"
                name="inheritanceMode"
                value="composed"
                [checked]="inheritanceMode === 'composed'"
                (click)="selectInheritanceMode('composed')"
                [disabled]="!canEdit || busy || comparingCurrent()"
              />
              <span
                ><strong>Keep linked</strong
                ><small
                  >Future concept changes continue to flow into this
                  phrase.</small
                ></span
              >
            </label>
            <label
              [class.inheritance-option--selected]="
                inheritanceMode === 'literal'
              "
            >
              <input
                type="radio"
                name="inheritanceMode"
                value="literal"
                [checked]="inheritanceMode === 'literal'"
                (click)="selectInheritanceMode('literal')"
                [disabled]="
                  !canEdit ||
                  busy ||
                  comparingCurrent() ||
                  fixedWordingRestriction !== null
                "
              />
              <span
                ><strong>Use fixed wording</strong
                ><small>{{
                  fixedWordingRestriction ||
                    "Freeze the expanded text and stop inheriting concept changes."
                }}</small></span
              >
            </label>
          </fieldset>

          <details class="draft-note">
            <summary>
              Add a change note
              <span>{{ changeReason ? "Added" : "Optional" }}</span>
            </summary>
            <label class="editor-field">
              <span class="field-label"
                ><span>Change note</span
                ><span *ngIf="changeReason.length >= 400"
                  >{{ changeReason.length }} / 500</span
                ></span
              >
              <textarea
                #noteInput
                [(ngModel)]="changeReason"
                rows="2"
                maxlength="500"
                [disabled]="!canEdit || busy || comparingCurrent()"
                (focus)="focusEditField('note')"
                (compositionstart)="startComposition('note')"
                (compositionend)="finishComposition('note')"
                (beforeinput)="beginTextEdit('note', $event)"
                (blur)="endTypingGroup()"
                (ngModelChange)="onTextChanged('note')"
                placeholder="Why is this wording changing?"
              ></textarea>
              <small class="field-help"
                >This note is optional and is recorded in immutable change
                history.</small
              >
            </label>
          </details>
        </div>
      </section>
    </div>

    <section
      *ngSwitchCase="'impact'"
      class="secondary-workspace"
      aria-labelledby="impact-workspace-heading"
    >
      <header class="secondary-workspace__header">
        <div>
          <h2 id="impact-workspace-heading">Where this wording is used</h2>
          <span
            >Source declarations and authorized saved template versions.
            Retained outputs keep their captured wording.</span
          >
        </div>
        <button type="button" (click)="selectWorkspace('edit')">
          Back to editing
        </button>
      </header>
      <p
        class="usage-empty"
        *ngIf="
          detail.authoredDocumentUsageDiscovery &&
          !detail.authoredDocumentUsageDiscovery.isComplete
        "
        data-testid="terminology-discovery-coverage"
      >
        Discovery is incomplete. Local sources, unsupported versions and
        context-specific dependencies may be absent; no matches does not
        establish that wording is unused.
      </p>
      <div
        class="complete-usage-list"
        *ngIf="usageRows.length > 0; else noCompleteUsages"
      >
        <article
          class="complete-usage-row"
          *ngFor="let usage of usageRows; trackBy: trackUsage"
          [class.complete-usage-row--selected]="usage.isSelected"
        >
          <span class="complete-usage-row__surface">{{
            toDisplayLabel(usage.surfaceCode)
          }}</span>
          <span
            ><strong>{{ toDisplayLabel(usage.featureCode) }}</strong
            ><small>{{ usage.componentCode }}</small></span
          >
          <code [title]="usage.locationSummary">{{
            usage.locationSummary
          }}</code>
          <span class="usage-row__origin" *ngIf="usage.isSelected"
            >Opened here</span
          >
          <a *ngIf="usage.route" [href]="usage.route">Open</a>
          <details
            *ngIf="usage.isAuthoredTemplate"
            class="authored-usage-details"
          >
            <summary>Version and dependency evidence</summary>
            <dl>
              <ng-container *ngFor="let location of usage.locations">
                <dt>{{ toDisplayLabel(location.kind) }}</dt>
                <dd>{{ location.value }}</dd>
              </ng-container>
            </dl>
          </details>
        </article>
      </div>
      <ng-template #noCompleteUsages>
        <div class="usage-empty">
          No usage locations have been discovered for this definition; discovery
          may be incomplete.
        </div>
      </ng-template>
    </section>

    <section
      *ngSwitchCase="'history'"
      class="secondary-workspace"
      aria-labelledby="history-workspace-heading"
    >
      <header class="secondary-workspace__header">
        <div>
          <h2 id="history-workspace-heading">Activity and runtime health</h2>
          <span
            >Immutable wording history and operational diagnostics for this
            definition.</span
          >
        </div>
        <button type="button" (click)="selectWorkspace('edit')">
          Back to editing
        </button>
      </header>
      <div class="editor-loading" *ngIf="isHistoryLoading()" role="status">
        Loading revision history…
      </div>
      <div
        class="editor-message editor-message--error"
        *ngIf="historyError() as error"
        role="alert"
      >
        <div>
          <strong>{{ error.title }}</strong
          ><span *ngFor="let message of error.messages">{{ message }}</span>
        </div>
        <button type="button" (click)="refreshRevisionHistory()">Retry</button>
      </div>
      <phms-platform-terminology-history-health-panel
        *ngIf="!isHistoryLoading() && !historyError()"
        [detail]="detail"
        [history]="revisionHistory()"
        (historyRefreshRequested)="refreshRevisionHistory()"
      >
      </phms-platform-terminology-history-health-panel>
    </section>

    <section
      *ngSwitchCase="'technical'"
      class="secondary-workspace technical-workspace"
      aria-labelledby="technical-workspace-heading"
    >
      <header class="secondary-workspace__header">
        <div>
          <h2 id="technical-workspace-heading">Technical details</h2>
          <span
            >Definition identity, constraints, and active revision
            metadata.</span
          >
        </div>
        <button type="button" (click)="selectWorkspace('edit')">
          Back to editing
        </button>
      </header>
      <dl class="technical-details">
        <div>
          <dt>Token</dt>
          <dd>
            <code>{{ definition.tokenCode }}</code>
          </dd>
        </div>
        <div>
          <dt>Kind</dt>
          <dd>{{ toDisplayLabel(definition.kindCode) }}</dd>
        </div>
        <div *ngIf="definition.familyCode">
          <dt>Family</dt>
          <dd>{{ toDisplayLabel(definition.familyCode) }}</dd>
        </div>
        <div *ngIf="definition.grammaticalFormCode">
          <dt>Form</dt>
          <dd>{{ toDisplayLabel(definition.grammaticalFormCode) }}</dd>
        </div>
        <div>
          <dt>Classification</dt>
          <dd>{{ toDisplayLabel(definition.classificationCode) }}</dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{{ toDisplayLabel(definition.ownerCode) }}</dd>
        </div>
        <div>
          <dt>Configuration</dt>
          <dd>{{ definition.configurationCode }}</dd>
        </div>
        <div>
          <dt>Scope</dt>
          <dd>{{ scopeLabel(definition) }}</dd>
        </div>
        <div>
          <dt>References</dt>
          <dd>
            {{ detail.constraints.allowReferences ? "Allowed" : "Not allowed" }}
          </dd>
        </div>
        <div>
          <dt>Content length</dt>
          <dd>{{ minimumLength ?? 1 }}–{{ maximumLength }} characters</dd>
        </div>
        <div>
          <dt>Active revision</dt>
          <dd>
            {{
              definition.activeRevisionId
                ? "rev-" + definition.activeRevisionId
                : "None"
            }}
          </dd>
        </div>
      </dl>
      <p class="constraint-warning" *ngIf="!detail.constraints.isValid">
        Constraint metadata is invalid. Saving is blocked.
      </p>
    </section>
  </ng-container>
</div>
